import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n<Shared::Modal @close={{this.close}} @focusTrap={{true}} class=\"is--top\" data-test-id=\"confirmation-modal\">\n  <div class=\"o--modalDialog\" {{did-insert this.initFocusOnConfirmButton}}>\n    <h1 class=\"o--modalDialog__title qa--modal_message\">\n      {{this.modal.message}}\n    </h1>\n    <p class=\"o--modalDialog__text qa--modal_description\">\n      {{html-safe this.modal.description}}\n      {{#if this.modal.learnMoreLink}}\n        <span>\n          <Shared::Link\n            @href={{this.modal.learnMoreLink}}\n            @text={{t \"shared_phrases.learn_more\"}}\n            class=\"u--text--white u--text--underline\"\n          />\n        </span>\n      {{/if}}\n    </p>\n    {{#if this.modal.doNotShowAgainKey}}\n      <div class=\"o--form__checkbox u--inline u--m__t__0 u--m__b__lg\" data-test-id=\"do-not-show-again-checkbox\">\n        <label>\n          <Input\n            @checked={{this.doNotShowAgainChecked}}\n            @type=\"checkbox\"\n            class=\"o--form__checkbox\"\n            id=\"dontShowThisAgain\"\n          />\n          {{t \"shared_phrases.dont_show_this_again\"}}\n        </label>\n      </div>\n    {{/if}}\n    <div class=\"o--btnBar\">\n      <Shared::Ui::Button\n        @class={{concat-classes \"qa--modal_cancel_btn\" (if this.modal.cancelButtonClass this.modal.cancelButtonClass)}}\n        @onClick={{this.close}}\n        @size=\"large\"\n        @text={{this.modal.cancelButton}}\n        @variant=\"mute\"\n        type=\"button\"\n      />\n      <Shared::Ui::Button\n        @class={{concat-classes\n          \"qa--modal_confirm_btn\"\n          (if this.modal.confirmButtonClass this.modal.confirmButtonClass)\n        }}\n        @onClick={{this.confirm}}\n        @size=\"large\"\n        @text={{this.modal.confirmButton}}\n        @variant=\"primary\"\n        id=\"e--confirmButton\"\n        type=\"button\"\n      />\n    </div>\n  </div>\n</Shared::Modal>", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n<Shared::Modal @close={{this.close}} @focusTrap={{true}} class=\"is--top\" data-test-id=\"confirmation-modal\">\n  <div class=\"o--modalDialog\" {{did-insert this.initFocusOnConfirmButton}}>\n    <h1 class=\"o--modalDialog__title qa--modal_message\">\n      {{this.modal.message}}\n    </h1>\n    <p class=\"o--modalDialog__text qa--modal_description\">\n      {{html-safe this.modal.description}}\n      {{#if this.modal.learnMoreLink}}\n        <span>\n          <Shared::Link\n            @href={{this.modal.learnMoreLink}}\n            @text={{t \"shared_phrases.learn_more\"}}\n            class=\"u--text--white u--text--underline\"\n          />\n        </span>\n      {{/if}}\n    </p>\n    {{#if this.modal.doNotShowAgainKey}}\n      <div class=\"o--form__checkbox u--inline u--m__t__0 u--m__b__lg\" data-test-id=\"do-not-show-again-checkbox\">\n        <label>\n          <Input\n            @checked={{this.doNotShowAgainChecked}}\n            @type=\"checkbox\"\n            class=\"o--form__checkbox\"\n            id=\"dontShowThisAgain\"\n          />\n          {{t \"shared_phrases.dont_show_this_again\"}}\n        </label>\n      </div>\n    {{/if}}\n    <div class=\"o--btnBar\">\n      <Shared::Ui::Button\n        @class={{concat-classes \"qa--modal_cancel_btn\" (if this.modal.cancelButtonClass this.modal.cancelButtonClass)}}\n        @onClick={{this.close}}\n        @size=\"large\"\n        @text={{this.modal.cancelButton}}\n        @variant=\"mute\"\n        type=\"button\"\n      />\n      <Shared::Ui::Button\n        @class={{concat-classes\n          \"qa--modal_confirm_btn\"\n          (if this.modal.confirmButtonClass this.modal.confirmButtonClass)\n        }}\n        @onClick={{this.confirm}}\n        @size=\"large\"\n        @text={{this.modal.confirmButton}}\n        @variant=\"primary\"\n        id=\"e--confirmButton\"\n        type=\"button\"\n      />\n    </div>\n  </div>\n</Shared::Modal>","moduleName":"later/components/confirmation-modal.hbs","parseOptions":{"srcName":"later/components/confirmation-modal.hbs"}});
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isString } from 'lodash';

import { DialogType } from 'later/services/dialog-manager';

import type IntlService from 'ember-intl/services/intl';
import type CacheService from 'later/services/cache';
import type DialogManagerService from 'later/services/dialog-manager';
import type { Maybe } from 'shared/types';

interface ConfirmationModalComponentArgs {
  /** Object holding options/parameters for confirmation modal */
  model: {
    /** Title message of confirmation modal */
    message: Maybe<string>;
    /** Body text of confirmation modal */
    description: Maybe<string>;
    /** Cancel button text */
    cancelButton: Maybe<string>;
    /** Cancel button class selector */
    cancelButtonClass: Maybe<string>;
    /** Confirm button text */
    confirmButton: Maybe<string>;
    /** Confirm button class selector */
    confirmButtonClass: Maybe<string>;
    /** Do not show again button */
    doNotShowAgainKey: Maybe<string>;
    /** Learn More Link */
    learnMoreLink: Maybe<string>;
  };
}

export default class ConfirmationModalComponent extends Component<ConfirmationModalComponentArgs> {
  @service declare cache: CacheService;
  @service declare dialogManager: DialogManagerService;
  @service declare intl: IntlService;

  @tracked doNotShowAgainChecked = false;

  constructor(owner: unknown, args: ConfirmationModalComponentArgs) {
    super(owner, args);
    if (isString(this.doNotShowAgainCacheKey) && this.cache.retrieve(this.doNotShowAgainCacheKey)) {
      this.dialogManager.resolvePromise(DialogType.Confirmation);
    }
  }

  get defaults(): ConfirmationModalComponentArgs['model'] {
    return {
      message: null,
      description: null,
      cancelButton: this.intl.t('shared_words.cancel'),
      cancelButtonClass: null,
      confirmButton: this.intl.t('shared_words.ok'),
      confirmButtonClass: null,
      doNotShowAgainKey: null,
      learnMoreLink: null
    };
  }

  get doNotShowAgainCacheKey(): Maybe<string> {
    return this.modal.doNotShowAgainKey;
  }

  get modal(): ConfirmationModalComponentArgs['model'] {
    return Object.assign({}, this.defaults, this.args.model);
  }

  @action
  close(): void {
    this.dialogManager.rejectPromise(DialogType.Confirmation);
  }

  @action
  confirm(): void {
    if (isString(this.doNotShowAgainCacheKey) && this.doNotShowAgainChecked) {
      this.cache.add(this.doNotShowAgainCacheKey, true, {
        expiry: this.cache.maxExpiryDate(),
        persist: true
      });
    }
    this.dialogManager.resolvePromise(DialogType.Confirmation);
  }

  @action
  initFocusOnConfirmButton(): void {
    const element = document.getElementById('e--confirmButton');
    if (element) {
      element.focus();
    }
  }
}
